// lobby-view.ts — the HTML/DOM MULTIPLAYER flow, VIEW half (HTML/DOM UI shell
// plan, Phase 3). Renders LobbyController state to the DOM and routes clicks
// back to its actions; browser-only (the logic + wire boundary live in
// lobby-controller.ts, which is node-tested for wire-equivalence). FAITHFUL
// chrome — the SAME treatment the SP menu got (menu-view.ts): every MP screen
// is a 240×160 forms.ini design stage (formScale/makeStage/at), composited
// 9-slice buttons placed at authored coords, the zip-in cascade on a screen
// change, and the solid steel-blue list selection bar. connecting → lobby
// (room list, form 1073) → room (game list + chat, form 1034) → staging
// (roster + ready/begin + add-bot + chat, form 1026). While a launched game
// runs the view hides and the canvas shows (inGame), exactly like the SP menu.
//
// The composited 9-slice button (button/composite) is now the SHARED chrome.ts
// makeShellButton factory (de-dup of the menu/lobby/dlc copies). The remaining
// makeStage/at/zipIn/listItem helpers stay local to each faithful screen-set.

import type { LobbyController, MpMissionRow, MpMissionCategory } from './lobby-controller.ts';
import type { ServerInfo } from '../ui/chooseserverform.ts';
import { GetDisplayName } from '../ui/gameform.ts';
import { GetSideColor } from '../gfx/palette.ts';
import { type ButtonArt } from './title-art.ts';
import {
  ksfxGuiButtonTap,
  ksfxGuiCheckBoxTap,
  ksfxGuiScrollingListSelectItem,
} from '../ui/uikit.ts';
import { zipInStage } from './formchrome.ts';
import { GetSpeedMultiplierString, gatGameSpeeds } from '../ui/gameoptions.ts';
import { kszRegisterUrl, kszForgotAccountUrl } from '../ui/loginform.ts';
import {
  COL_BG,
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  formScale,
  FONT_BUTTON,
  FONT_BODY,
  buttonWidthU,
  makeShellButton,
  formTitle,
  ensureA11yStyles,
  centeredBand,
  isPortraitBoost,
  stageTextInput,
  onSelectClick,
  mountViewportFit,
  type ViewportFit,
  withFocusPreserved,
  autofocusOnce,
  type AutofocusOnce,
} from './chrome.ts';

export interface LobbyViewDeps {
  controller: LobbyController;
  root: HTMLElement;
  showCanvas: () => void;
  hideCanvas: () => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url. */
  titleBg?: string;
  /** the raw 9-slice button pieces — faithful chrome shared with the SP menu;
   *  absent ⇒ a flat CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sound effect (ksfxGui*) on press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** open an external URL in a new tab — the LOGIN form's REGISTER / FORGOT ACCOUNT
   *  buttons (account pages served by the live /accounts/* worker). The browser
   *  supplies window.open; the canvas used HostInitiateWebView for the same URLs. */
  openUrl?: (url: string) => void;
  /** BACK from the lobby → the DOM main menu (the host swaps views). */
  onExit: () => void;
  /** the error screen's one-tap RELOAD when the server rejected our protocol
   *  (controller.needsUpdate, #206) — the host wires forceReloadLatest(). */
  onReload?: () => void;
}

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

export class LobbyView {
  private readonly d: LobbyViewDeps;
  /** view-local selection state (the canvas list controls track a selected row;
   *  the controller's wire flow is untouched). */
  private selectedRoomId: number | null = null;
  /** true once the player has explicitly tapped a room row. Until then the
   *  selection keeps FOLLOWING the first listed room: the server streams the
   *  permanent rooms one LOBBYADDROOM at a time in ascending-id order (Main /
   *  Admin id=1 first, Main id=0xFFFFFFFF LAST), so a seed-once default locks
   *  onto whichever room happened to arrive before the first render — the
   *  "lobby opens with Main / Admin selected" bug. */
  private roomSelectedByUser = false;
  private selectedGameId: number | null = null;
  private selectedServer = 0;
  private selectedMapIndex: number | null = null;
  /** LOGIN form fields (the canvas MultiplayerLoginForm controls); anonymous-first
   *  like the web build's ReflectHandlerState (the name/password rows hide). */
  private loginName = '';
  private loginPassword = '';
  private loginAnonymous = true;
  /** CREATE ROOM form fields (the canvas CreateRoomForm controls). */
  private roomDraftName = '';
  private roomDraftPassword = '';
  private roomDraftPrivate = false;
  /** CREATE GAME game-speed notch (an index into gatGameSpeeds — the canvas MP
   *  slider range [2, len-1-3]); -1 until the screen seeds it from env speed. */
  private gameSpeedNotch = -1;
  /** CREATE GAME category tab (the canvas CreateGameForm Challenge|Add-On radio
   *  bar; kidcCategories). Seeded from the selected mission's category. */
  private createGameTab: MpMissionCategory = 'challenge';
  /** the last screen rendered — the zip-in plays only on a screen CHANGE. */
  private lastScreen: string | null = null;
  /** pending "form shown" SFX (fires at zip end; cleared on a screen change). */
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;
  /** keeps the form clear of the soft keyboard (chrome.mountViewportFit). */
  private readonly viewport: ViewportFit;
  /** JOIN-ROOM password draft. The prompt is rebuilt by every render, so the typed value
   *  needs to live on the VIEW (like loginPassword / roomDraftPassword) — reading it back
   *  off the input meant any re-render silently wiped what the user had typed. */
  private joinPasswordDraft = '';
  /** the CURRENT join-password ask, by identity — the controller allocates a fresh prompt
   *  object per ask (including the wrong-password re-ask), so this distinguishes a new ask
   *  from a mere re-render without a hand-rolled key. */
  private joinPrompt: object | null = null;
  private readonly joinAutofocus: AutofocusOnce = autofocusOnce();
  /** the shared 9-slice shell ButtonControl factory (chrome.ts) — its own composite
   *  cache + up/down state swap + ksfxGuiButtonTap. */
  private readonly btn: (
    label: string,
    onClick: () => void,
    enabled?: boolean,
    size?: { w: number; h: number },
  ) => HTMLButtonElement;

  constructor(d: LobbyViewDeps) {
    this.d = d;
    // Pre-fill the LOGIN form with the remembered (non-anonymous) name and default
    // to the named-login tab, so a returning player whose saved credentials need a
    // re-entry (or who backed out of an auto-login) just types their password —
    // they don't land on the anonymous-first form and re-type their whole identity.
    const savedName = d.controller.savedUsername();
    if (savedName.length > 0) {
      this.loginName = savedName;
      this.loginAnonymous = false;
    }
    this.btn = makeShellButton(d, 'sub');
    ensureA11yStyles(); // the global keyboard :focus-visible ring (once)
    const s = d.root.style;
    s.position = 'fixed';
    // inset:0 is the RESTING box; chrome.mountViewportFit then narrows the root to the VISUAL
    // (left/top/width/height, with right/bottom back to auto) so the form sits in the band
    // ABOVE an open soft keyboard. Keeping the shorthand makes the fit purely additive — the
    // root is already correct before the first fit. Note the flex centring below is
    // incidental: makeStage() positions the stage absolutely at 50%/50% and this fixed root
    // is its containing block, so the form centres on the ROOT'S BOX — which is exactly why
    // resizing that box is what moves the form clear of the keyboard.
    s.inset = '0';
    s.display = 'flex';
    s.flexDirection = 'column';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.background = COL_BG;
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.zIndex = '10';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    // The MP forms are fixed 240×160 stages centered in the window, so a real size /
    // orientation change re-renders to rescale the design stage (the canvas re-lays the form
    // too) — but a HEIGHT-ONLY change is the soft keyboard, and re-rendering there would
    // replaceChildren() away the very <input> the keyboard just opened for, dismissing it.
    // (This was the "can't get the keyboard to stay up on the MP login" report.)
    this.viewport = mountViewportFit(d.root, {
      rebuild: () => this.render(),
      // while a match runs the root is display:none (render() early-returns) — don't bother
      skip: () => this.d.controller.inGame,
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  // Escape → the current screen's BACK action (the SAME thing the on-screen BACK/CANCEL/
  // LEAVE button does), matching the in-game dialogs' Esc wiring (confirm-view.ts). An open
  // "Enter Password" prompt takes Escape FIRST (its CANCEL).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    const c = this.d.controller;
    if (c.inGame) return; // hidden while the launched game runs — leave Esc to the game
    e.preventDefault();
    if (c.passwordPrompt !== null) {
      c.cancelJoinPassword();
      return;
    }
    switch (c.screen) {
      case 'createroom':
        c.cancelCreateRoom();
        break; // → lobby
      case 'creategame':
        c.cancelCreateGame();
        break; // → room
      case 'room':
        void c.leaveRoom();
        break; // → lobby
      case 'staging':
        void c.leaveGame();
        break; // → room/lobby
      // connecting / chooseserver / login / lobby / error all BACK out of MP entirely.
      default:
        this.d.onExit();
        break;
    }
  };

  /** detach the listeners (a re-created view must not leak ones bound to the stale
   *  instance). */
  destroy(): void {
    this.viewport.detach();
    this.joinAutofocus.cancel();
    window.removeEventListener('keydown', this.onKeyDown);
  }

  render(): void {
    const c = this.d.controller,
      root = this.d.root;
    if (c.inGame) {
      root.style.display = 'none';
      this.lastScreen = null;
      this.d.showCanvas();
      return;
    }
    this.d.hideCanvas();
    root.style.display = 'flex';
    // EVERY MP screen renders over the title backdrop, like the canvas ShellForm
    // (shellform.ts OnPaintBackground draws titlescreenbkgd.png with a letterbox).
    root.style.backgroundImage = this.d.titleBg !== undefined ? `url(${this.d.titleBg})` : 'none';
    root.style.backgroundColor = COL_BG;
    root.style.backgroundSize = 'cover';
    root.style.backgroundPosition = 'center';
    root.style.backgroundRepeat = 'no-repeat';
    let screen: HTMLElement;
    switch (c.screen) {
      case 'chooseserver':
        screen = this.chooseServerScreen();
        break;
      case 'login':
        screen = this.loginScreen();
        break;
      case 'lobby':
        screen = this.lobbyScreen();
        break;
      case 'createroom':
        screen = this.createRoomScreen();
        break;
      case 'room':
        screen = this.roomScreen();
        break;
      case 'creategame':
        screen = this.createGameScreen();
        break;
      case 'staging':
        screen = this.stagingScreen();
        break;
      case 'error':
        screen = this.errorScreen();
        break;
      default:
        screen = this.connectingScreen();
    }
    const changed = c.screen !== this.lastScreen;

    // Selecting a list row re-renders the whole screen, which rebuilds the
    // scrolling lists fresh (scrollTop 0). On a same-screen re-render, carry the
    // old scroll position over so picking a map (or room/game) doesn't snap the
    // list back to the top. Lists keep their order across a re-render, so match
    // them by position. (On a screen CHANGE the list set differs — let it reset.)
    const prevScroll = changed
      ? []
      : Array.from(root.querySelectorAll<HTMLElement>('[data-list]')).map((e) => e.scrollTop);
    // Same idea for the TEXT FIELDS: replaceChildren destroys the focused <input>, which blurs
    // it and drops the soft keyboard (chrome.withFocusPreserved carries focus + caret across).
    withFocusPreserved(root, () => {
      root.replaceChildren(screen);
      // a FRESH stage carries none of the keyboard fit, so re-apply it BEFORE the scroll
      // restore below (a scrollTop write forces layout — do it at the final size, not the
      // stale one). Otherwise a rebuild while the keyboard is up snaps the form back.
      this.viewport.fit();
      if (!changed) {
        const lists = screen.querySelectorAll<HTMLElement>('[data-list]');
        lists.forEach((e, i) => {
          if (prevScroll[i] !== undefined) e.scrollTop = prevScroll[i];
        });
      }
    });

    this.lastScreen = c.screen;
    if (changed) this.zipIn(screen);
  }

  // ── the faithful design stage (forms.ini: the shell/MP forms are 240×160) ────
  // Copied from menu-view.ts (the verified-faithful SP chrome): a FIXED
  // magnification (≈2 game px per design unit × gameScale), centered, shrinking
  // only when a 240×160 form (+margin) won't fit a small window.
  // a wUnits×160 design stage centered in the window (the ShellForm coordinate
  // space). Most MP forms are 240 wide; CreateGame (kidfCreateGame) is 160 wide,
  // so it passes wUnits=160 — the scale is the SAME (the 240-form fit budget), the
  // narrower form just centers with more backdrop either side, like the canvas.
  private makeStage(wUnits = 240): { stage: HTMLElement; s: number } {
    const s = formScale();
    const stage = el('div', {
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: `${wUnits * s}px`,
      height: `${160 * s}px`,
      transform: 'translate(-50%, -50%)',
    });
    return { stage, s };
  }

  // position a child at forms.ini design coords (x, y[, w, h]) within the stage.
  private at(
    child: HTMLElement,
    s: number,
    x: number,
    y: number,
    w?: number,
    h?: number,
  ): HTMLElement {
    Object.assign(child.style, { position: 'absolute', left: `${x * s}px`, top: `${y * s}px` });
    if (w !== undefined) child.style.width = `${w * s}px`;
    if (h !== undefined) child.style.height = `${h * s}px`;
    return child;
  }

  // a button placed at forms.ini design coords (x, y) with a w×h-unit composited
  // bitmap (the shell ButtonControl is 12u tall in the MP forms — narrower than
  // the menu's 14u — so the caller passes h explicitly).
  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
    en = true,
  ): HTMLElement {
    return this.at(this.btn(label, on, en, { w: w * s, h: h * s }), s, x, y);
  }

  // a bottom BUTTON ROW that AUTO-SIZES each button to its label. The wider WI button
  // face overflowed the fixed forms.ini widths (e.g. "JOIN ROOMCREATE ROOMSIGN OUT"
  // jamming together), so size each box to its measured label width (+ horizontal
  // padding, clamped to a min), then centre the whole row within the [bandX, bandX+
  // bandW] design band with even gaps so nothing overlaps. Keeps the row's y + the
  // overall centring; only the widths/spacing fit the text. Filtered to shown buttons.
  private buttonRow(
    stage: HTMLElement,
    s: number,
    y: number,
    h: number,
    bandX: number,
    bandW: number,
    btns: { label: string; on: () => void; en?: boolean; minW?: number }[],
  ): void {
    // size each button to its label via the SHARED buttonWidthU (fonts.ts) — the same
    // measured sizer game-options + every button row uses, so none collide with the frame.
    // At small (phone) scales buttonWidthU's 9px font-floor inflates the design-unit widths,
    // so a 4-button row (JOIN ROOM/CREATE ROOM/SIGN OUT/BACK) overran the 236u band and the
    // last button was pushed off the right edge of the 240u form (clipped by overflow:hidden
    // → the missing "BACK"). Fit the row to the band: trim the per-button breathing-room pad
    // (down to the 9-slice caps), then the gap, then proportionally scale, so every button
    // stays on-screen at any scale. At desktop scale none of this triggers (the row fits).
    const widthsAt = (padU: number): number[] =>
      btns.map((b) => buttonWidthU(b.label, h, s, b.minW ?? 36, padU));
    const sumOf = (ws: number[], gap: number): number =>
      ws.reduce((a, w) => a + w, 0) + gap * (ws.length - 1);
    const defaultPad = Math.round(h * 0.9) + 5; // buttonWidthU's default: caps (~0.9h) + 5u margin
    const minPad = Math.round(h * 0.9); // caps only — the label still clears the frame
    let gapU = 5;
    let pad = defaultPad;
    let ws = widthsAt(pad);
    // 1) spend the breathing-room margin first (labels stay fully inside the button).
    while (sumOf(ws, gapU) > bandW && pad > minPad) {
      pad -= 1;
      ws = widthsAt(pad);
    }
    // 2) then tighten the inter-button gap.
    if (sumOf(ws, gapU) > bandW) gapU = 2;
    // 3) last resort for very long rows (e.g. JOIN ROOM/CREATE ROOM/SIGN OUT/BACK in the narrow
    //    portrait band): shrink the button HEIGHT, which drives the label FONT (0.46×h in
    //    makeShellButton), then recompute widths — so the labels shrink WITH their frames.
    //    Scaling only the widths (the old behaviour) kept the font full-size, so the labels
    //    overran their narrowed frames and collided (the squashed/overlapping bottom row).
    if (sumOf(ws, gapU) > bandW) {
      const gapsW = gapU * (ws.length - 1);
      const scale = (bandW - gapsW) / (sumOf(ws, gapU) - gapsW);
      h = Math.max(1, h * scale);
      ws = widthsAt(minPad);
      const total = sumOf(ws, gapU); // the buttonWidthU font-floor can leave it a hair over → clamp
      if (total > bandW) {
        const k = (bandW - gapsW) / (total - gapsW);
        ws = ws.map((w) => Math.max(1, Math.floor(w * k)));
      }
    }
    // centre the (now band-fitting) row.
    let x = bandX + Math.max(0, (bandW - sumOf(ws, gapU)) / 2);
    btns.forEach((b, i) => {
      stage.append(this.stageButton(s, b.label, x, y, ws[i], h, b.on, b.en ?? true));
      x += ws[i] + gapU;
    });
  }

  // the CHAT button label — a steady "•" notify badge while there's unread chat
  // (the canvas chatter.cpp blinks the whole CHAT button; a steady badge is the DOM
  // unread-indicator, and keeps the per-label-sized button row from jittering as the
  // marker toggled). Opening the overlay clears chatUnread (controller.showChat).
  private chatLabel(): string {
    return this.d.controller.chatUnread ? 'CHAT •' : 'CHAT';
  }

  // the ShellForm zip-in (formchrome.ts zipInStage ↔ shellform.ts:298-384): EVERY
  // control on the form cascades to its place from an alternating side (odd→left,
  // even→right), staggered, then ksfxGuiFormShow plays. The room/game LIST is one
  // control here, so it zips as a single unit (its rows do NOT cascade individually).
  private zipIn(screen: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(screen, this.d.playSfx);
  }

  // ── screens ─────────────────────────────────────────────────────────────────
  // connecting / error — transient, simple centered text on the stage.
  private connectingScreen(): HTMLElement {
    const { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('MULTIPLAYER', s), s, 2, 6, 236));
    const b = centeredBand(20, 200);
    stage.append(
      this.at(this.centerText(this.d.controller.status || 'Connecting…', s), s, b.x, 78, b.w),
    );
    return stage;
  }

  private errorScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('MULTIPLAYER', s), s, 2, 6, 236));
    const eb = centeredBand(20, 200);
    stage.append(
      this.at(this.centerText(c.error || 'Multiplayer is unavailable.', s), s, eb.x, 70, eb.w),
    );
    // a protocol mismatch means THIS BUILD is stale (#206) — offer the one-tap
    // RELOAD (→ forceReloadLatest) beside BACK.
    if (c.needsUpdate && this.d.onReload !== undefined) {
      const onReload = this.d.onReload;
      this.buttonRow(stage, s, 140, 12, eb.x, eb.w, [
        {
          label: 'RELOAD',
          on: () => {
            onReload();
          },
        },
        {
          label: 'BACK',
          on: () => {
            this.d.onExit();
          },
        },
      ]);
      return stage;
    }
    // BACK — sized to its label + centred on the 240-form (was a fixed 40u box).
    const wBack = buttonWidthU('BACK', 12, s);
    stage.append(
      this.stageButton(s, 'BACK', (240 - wBack) / 2, 140, wBack, 12, () => this.d.onExit()),
    );
    return stage;
  }

  // LobbyForm (forms.ini kidfLobby 1073): "Player name:" + the name (top-left),
  // the online/lurker count (top-right), the room LIST, then JOIN ROOM /
  // CREATE ROOM / SIGN OUT / BACK across the bottom.
  private lobbyScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    const rooms = c.roomList();
    // keep the view-local selection valid (default the first room). Until the player
    // picks a row themselves the default keeps tracking rooms[0] (roomList() sorts by
    // id DESC, so once Main — the LAST room the server advertises — arrives, the
    // default lands on it rather than sticking to the first arrival, Main / Admin).
    if (
      !this.roomSelectedByUser ||
      this.selectedRoomId === null ||
      !rooms.some((r) => r.roomid === this.selectedRoomId)
    ) {
      this.selectedRoomId = rooms[0]?.roomid ?? null;
    }

    stage.append(this.at(formTitle('MULTIPLAYER LOBBY', s), s, 0, 6, 240));
    // forms.ini kidfLobby: a fixed "Player name:" label + the name value. The label
    // clipped in the authored 46u slot under the wider WI font, so widen it to 56u and
    // shift the name value from x=48 → x=58 (the value box has ample room).
    const count = el('div', { ...labelStyle(s), textAlign: 'right' });
    count.textContent = `${c.lurkerCount} online`;
    if (isPortraitBoost()) {
      // stack the name row + the online count into the centered band (the three-column
      // label/value/count row won't fit a 160u band side-by-side).
      const lb = centeredBand(2, 236);
      stage.append(this.at(this.label(`Player name: ${c.playerName}`, s), s, lb.x, 18, lb.w));
      stage.append(this.at(count, s, lb.x, 27, lb.w));
    } else {
      stage.append(this.at(this.label('Player name:', s), s, 2, 18, 56));
      stage.append(this.at(this.label(c.playerName, s), s, 58, 18, 180));
      stage.append(this.at(count, s, 138, 18, 100));
    }

    // the room list control (2,34,236,103) — selectable rows, double-click joins.
    // A private room carries a trailing PRIVATE marker + a lock glyph (the canvas
    // OnTimeout appends a "\tPRIVATE" column for priv rooms; lobbyform.ts:335-337).
    const lst = centeredBand(2, 236);
    stage.append(
      this.at(
        this.listBox(
          rooms.map((r) => ({
            id: r.roomid,
            text: `${r.priv ? '\u{1F512} ' : ''}${r.name}   (${r.cPlayers} player${r.cPlayers === 1 ? '' : 's'}, ${r.cGames} game${r.cGames === 1 ? '' : 's'})${r.priv ? '   — PRIVATE' : ''}`,
            selected: r.roomid === this.selectedRoomId,
            onSelect: () => {
              this.selectedRoomId = r.roomid;
              this.roomSelectedByUser = true;
            },
            onActivate: () => {
              void c.joinRoom(r.roomid);
            },
          })),
          'No rooms yet — create one.',
          s,
          true,
          'Rooms',
        ),
        s,
        lst.x,
        34,
        lst.w,
        103,
      ),
    );

    // the "Enter Password:" prompt for a private room (the canvas HostInitiateAsk;
    // lobbyform.cpp:131-134) — a modal overlay on the lobby stage.
    if (c.passwordPrompt !== null) stage.append(this.passwordPromptBox(c.passwordPrompt));
    else this.joinPasswordDraft = ''; // don't hold a password after the ask is dismissed

    // a join-failure status line (the canvas RoomForm.ShowJoinMessage box) — shown
    // only when no password prompt is up and a status (e.g. "The room is full") is set.
    // A one-line banner sitting ABOVE the button row with pointer-events off: the old y=132
    // centred line ran into the y=140 JOIN ROOM/CREATE ROOM/SIGN OUT row at phone scale (the
    // "Could not join the room message appears over other form elements" report).
    const bn = centeredBand(20, 200);
    if (c.passwordPrompt === null && c.status.length > 0) {
      stage.append(this.at(this.statusBanner(c.status, s), s, bn.x, 126, bn.w, 9));
    } else if (c.passwordPrompt === null && c.lobbyChat.length > 0) {
      // the LOBBY has no chat panel (game/lobbyform.cpp:383-386 pops a transient
      // msgbox per line) — surface the latest incoming lobby line in the same banner.
      const note = this.statusBanner(c.lobbyChat[c.lobbyChat.length - 1], s);
      note.style.fontStyle = 'italic';
      note.style.opacity = '0.9';
      stage.append(this.at(note, s, bn.x, 126, bn.w, 9));
    }

    const join = (): void => {
      if (this.selectedRoomId !== null) void c.joinRoom(this.selectedRoomId);
    };
    // auto-sized bottom row (JOIN ROOM / CREATE ROOM / SIGN OUT / BACK) — the fixed
    // forms.ini widths jammed under the wider WI font; size each to its label + space.
    const rb = centeredBand(2, 236);
    this.buttonRow(stage, s, 140, 12, rb.x, rb.w, [
      { label: 'JOIN ROOM', on: join, en: this.selectedRoomId !== null },
      {
        label: 'CREATE ROOM',
        on: () => {
          this.roomDraftName = '';
          this.roomDraftPassword = '';
          this.roomDraftPrivate = false;
          c.openCreateRoom();
        },
      },
      {
        label: 'SIGN OUT',
        on: () => {
          void c.signOut();
        },
      },
      { label: 'BACK', on: () => this.d.onExit() },
    ]);
    return stage;
  }

  // RoomForm (forms.ini kidfRoom 1034): the room-name label, the game LIST, then
  // JOIN GAME / CREATE GAME / CHAT / BACK; CHAT toggles a chat panel.
  private roomScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    const games = c.gameList();
    if (this.selectedGameId === null || !games.some((g) => g.gameid === this.selectedGameId)) {
      this.selectedGameId = games[0]?.gameid ?? null;
    }

    stage.append(this.at(formTitle('GAME ROOM', s), s, 0, 6, 240));
    const rl = centeredBand(2, 236);
    stage.append(this.at(this.label(`Room:  ${c.roomName()}`, s), s, rl.x, 18, rl.w));

    stage.append(
      this.at(
        this.listBox(
          games.map((g) => ({
            id: g.gameid,
            text: `${g.title}   ${g.cur}/${g.max}  (host ${g.creator})${g.cur >= g.max ? '  — full' : ''}`,
            selected: g.gameid === this.selectedGameId,
            onSelect: () => {
              this.selectedGameId = g.gameid;
            },
            onActivate: () => {
              void c.joinGame(g.gameid);
            },
          })),
          'No games yet — create one.',
          s,
          true,
          'Games',
        ),
        s,
        rl.x,
        34,
        rl.w,
        103,
      ),
    );

    const join = (): void => {
      if (this.selectedGameId !== null) void c.joinGame(this.selectedGameId);
    };
    // auto-sized bottom row (JOIN GAME / CREATE GAME / CHAT / BACK) — fit the wider font.
    this.buttonRow(stage, s, 140, 12, rl.x, rl.w, [
      { label: 'JOIN GAME', on: join, en: this.selectedGameId !== null },
      {
        label: 'CREATE GAME',
        on: () => {
          this.gameSpeedNotch = -1;
          this.selectedMapIndex = null;
          this.createGameTab = 'challenge';
          c.openCreateGame();
        },
      },
      { label: this.chatLabel(), on: () => c.showChat(), minW: 36 },
      {
        label: 'BACK',
        on: () => {
          void c.leaveRoom();
        },
      },
    ]);
    return stage;
  }

  // GameForm / staging (forms.ini kidfGame 1026): Game/Map/# players/Game Speed
  // detail labels, the creator-only ADD OPPONENT BOT accent button above the
  // roster, the PLAYERS header + roster LIST, then READY|BEGIN / CHAT / CANCEL.
  private stagingScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    const roster = c.roster();

    // the title gets the full 240-form width (one line, item 9) — the long heading
    // wrapped to 3 lines in the old 91-unit box and overlapped the detail labels.
    stage.append(this.at(formTitle('WAIT FOR JOINING PLAYERS', s), s, 0, 6, 240));
    if (isPortraitBoost()) {
      // stack all four detail labels into ONE column in the centered band — the authored
      // two-column layout (Game Speed: at x=122, right edge 212) overruns the 160u band.
      const db = centeredBand(42, 156);
      stage.append(this.at(this.detail('Game:', c.gameTitle(), s), s, db.x, 18, db.w));
      stage.append(this.at(this.detail('Map:', c.mapName(), s), s, db.x, 29, db.w));
      stage.append(
        this.at(
          this.detail('# players:', `${roster.length}/${c.maxPlayers()}`, s),
          s,
          db.x,
          40,
          db.w,
        ),
      );
      stage.append(
        this.at(
          this.detail('Game Speed:', GetSpeedMultiplierString(c.gameSpeed()), s),
          s,
          db.x,
          51,
          db.w,
        ),
      );
    } else {
      stage.append(this.at(this.detail('Game:', c.gameTitle(), s), s, 42, 18, 156));
      stage.append(this.at(this.detail('Map:', c.mapName(), s), s, 42, 29, 156));
      stage.append(
        this.at(this.detail('# players:', `${roster.length}/${c.maxPlayers()}`, s), s, 42, 40, 70),
      );
      stage.append(
        this.at(
          this.detail('Game Speed:', GetSpeedMultiplierString(c.gameSpeed()), s),
          s,
          122,
          40,
          90,
        ),
      );
    }

    // ADD OPPONENT BOT — a short accent button above the roster (creator only), sized
    // to its (long) label via buttonWidthU + centred so the text clears the 9-slice
    // frame (the fixed 120u box cramped the wide WI face). h=11 (the authored height).
    // Under the portrait boost the details stack down to y=51, so drop the button to y=64
    // to clear them (it still sits above the PLAYERS header at y=77). When a bot roster row
    // is selected the button becomes REMOVE BOT (removing that bot); width is pinned to the
    // wider ADD label so it doesn't reflow as it toggles. ADD is hidden once every joinable
    // slot is filled (gameform.cpp GetOpenSlotCount); REMOVE stays available even when full
    // (removing a bot is the only way to free a slot again).
    if (c.isCreator) {
      const selBotPid = c.selectedBotPid();
      const removing = selBotPid !== null;
      if (removing || c.openSlots() > 0) {
        const wBot = Math.max(
          buttonWidthU('ADD OPPONENT BOT', 11, s),
          buttonWidthU('REMOVE BOT', 11, s),
        );
        stage.append(
          this.stageButton(
            s,
            removing ? 'REMOVE BOT' : 'ADD OPPONENT BOT',
            (240 - wBot) / 2,
            isPortraitBoost() ? 64 : 52,
            wBot,
            11,
            removing ? () => c.removeBot(selBotPid) : () => c.addBot(),
          ),
        );
      }
    }

    const hdr = el('div', {
      ...labelStyle(s),
      textAlign: 'center',
      fontWeight: '700',
      letterSpacing: '0.08em',
    });
    hdr.textContent = 'PLAYERS';
    const hb = centeredBand(54, 132);
    stage.append(this.at(hdr, s, hb.x, 77, hb.w));

    // the roster list (52,87,136,46) — fixed-size, centered. Each row is "name (you)
    // is READY|not ready" (gameform.cpp:680-685) with the name tinted to the side's
    // palette color (GetSideColor; AI/bots show as "Apex") + a trailing "N more
    // player(s) needed" row (gameform.cpp:692-695) when short. The creator's BOT rows
    // are selectable — selecting one flips the button above to REMOVE BOT (human rows
    // stay inert).
    const selBot = c.selectedBotPid();
    const rows: {
      id: number;
      text: string;
      html: HTMLElement;
      selected: boolean;
      onSelect?: () => void;
    }[] = roster.map((p) => {
      const selectable = c.isCreator && p.bot;
      return {
        id: p.pid,
        text: '',
        html: this.rosterRow(p),
        selected: selectable && p.pid === selBot,
        onSelect: selectable ? () => c.toggleBotSelection(p.pid) : undefined,
      };
    });
    const needed = c.playersNeeded();
    if (needed > 0) {
      rows.push({ id: -1, text: '', html: this.neededRow(needed), selected: false });
    }
    const rb = centeredBand(52, 136);
    stage.append(
      this.at(
        this.listBox(rows, 'Waiting for players…', s, c.isCreator, 'Players'),
        s,
        rb.x,
        87,
        rb.w,
        46,
      ),
    );

    const beginOk = c.isCreator ? c.canBegin() : true;
    // auto-sized bottom row (BEGIN GAME|READY / CHAT / LEAVE) — "BEGIN GAME" overflowed
    // w=50; fit each + centre the row.
    const bb = centeredBand(2, 236);
    this.buttonRow(stage, s, 140, 12, bb.x, bb.w, [
      { label: c.isCreator ? 'BEGIN GAME' : 'READY', on: () => c.begin(), en: beginOk },
      { label: this.chatLabel(), on: () => c.showChat(), minW: 36 },
      {
        label: 'LEAVE',
        on: () => {
          void c.leaveGame();
        },
      },
    ]);
    return stage;
  }

  // a staging roster row "name (you) is READY|not ready" — the NAME itself is tinted to
  // the side's palette color (GetSideColor) rather than a separate "(color)" tag, and
  // AI/bot players show as "Apex" (the bot brain) instead of the raw "AI"/"Bot N" slot
  // name. The "(you)" hint stays neutral; one space separates the name from "is READY".
  // The row is plain INLINE flow (NOT display:flex): as flex items the spans would have
  // their leading/trailing whitespace trimmed, swallowing the " (you)"/" is" spaces.
  private rosterRow(p: {
    name: string;
    side: number;
    ready: boolean;
    bot: boolean;
    computer: boolean;
    me: boolean;
  }): HTMLElement {
    const row = el('div', { whiteSpace: 'nowrap' });
    const clr = GetSideColor(p.side);
    const namePart = el('span', { color: `rgb(${clr.r},${clr.g},${clr.b})`, fontWeight: '700' });
    namePart.textContent = GetDisplayName(p.name, p.bot || p.computer);
    row.append(namePart, `${p.me ? ' (you)' : ''} is ${p.ready ? 'READY' : 'not ready'}`);
    return row;
  }

  // the "N more player(s) needed" roster footer row (gameform.cpp:692-695).
  private neededRow(n: number): HTMLElement {
    const e = el('div', {
      width: '100%',
      whiteSpace: 'nowrap',
      opacity: '0.7',
      fontStyle: 'italic',
    });
    e.textContent = `${n} more player${n !== 1 ? 's' : ''} needed`;
    return e;
  }

  // ── CHOOSE SERVER (kidfChooseServer) ─────────────────────────────────────────
  // title (0,6,240) center; column headers Name/Location/Status/"# Players" at
  // y=20; the server LIST (2,32,236,111); CONNECT (40,141,50,12) / REFRESH
  // (100,141,50,12) / BACK (160,141,40,12). REFRESH re-runs the serverinfo fetch.
  private chooseServerScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    const servers = c.servers();
    if (this.selectedServer < 0 || this.selectedServer >= servers.length) this.selectedServer = 0;

    stage.append(this.at(formTitle('CHOOSE SERVER', s), s, 0, 6, 240));
    // the column headers (chooseserverform.PositionColumns) spread across the list. Under
    // the portrait boost the four columns are scaled+shifted into the centered band so they
    // stay on-screen and stay aligned with the (also band-narrowed) server rows.
    const boost = isPortraitBoost();
    if (boost) {
      const band = centeredBand(2, 236); // {40,160} — match serverRow's narrowed columns (k=160/236)
      const k = band.w / 236,
        x = band.x;
      stage.append(this.at(this.colHeader('Name', 'left', s), s, x + 2 * k, 20, 90 * k));
      stage.append(this.at(this.colHeader('Location', 'left', s), s, x + 94 * k, 20, 70 * k));
      stage.append(this.at(this.colHeader('Status', 'center', s), s, x + 156 * k, 20, 40 * k));
      stage.append(this.at(this.colHeader('# Players', 'right', s), s, x + 194 * k, 20, 40 * k));
    } else {
      stage.append(this.at(this.colHeader('Name', 'left', s), s, 4, 20, 90));
      stage.append(this.at(this.colHeader('Location', 'left', s), s, 96, 20, 70));
      stage.append(this.at(this.colHeader('Status', 'center', s), s, 158, 20, 40));
      stage.append(this.at(this.colHeader('# Players', 'right', s), s, 196, 20, 40));
    }

    const sb = centeredBand(2, 236);
    stage.append(
      this.at(
        this.listBox(
          servers.map((sv, i) => ({
            id: i,
            // a tabbed row: name, location, status, player count (the canvas ListControl
            // columns) — rendered as a flex row so the columns align with the headers.
            text: '',
            html: this.serverRow(sv, s, boost ? sb.w / 236 : 1),
            selected: i === this.selectedServer,
            onSelect: () => {
              this.selectedServer = i;
            },
            onActivate: () => {
              void c.selectServer(i);
            },
          })),
          'No servers available.',
          s,
          true,
          'Servers',
        ),
        s,
        sb.x,
        32,
        sb.w,
        111,
      ),
    );

    // CONNECT / REFRESH / BACK — sized PER-LABEL (the shared buttonRow) + centred, so
    // the wide WI face never overruns the fixed 50/40u boxes (was hand-tuned widths).
    this.buttonRow(stage, s, 141, 12, sb.x, sb.w, [
      {
        label: 'CONNECT',
        on: () => {
          void c.selectServer(this.selectedServer);
        },
        en: servers.length > 0,
      },
      {
        label: 'REFRESH',
        on: () => {
          void c.start();
        },
      },
      { label: 'BACK', on: () => this.d.onExit() },
    ]);
    return stage;
  }

  // ── MULTIPLAYER LOGIN (kidfLogin) ────────────────────────────────────────────
  // title (0,6,240) center; prompt (42,28,52); "Player Name:" (42,56,52) + EDIT
  // (88,56,90); "Password:" (42,84,52) + EDIT (88,84,90); "Login Anonymously"
  // CHECKBOX (42,112,57); LOGIN (10,140,36,12) / CANCEL (189,140,40,12). When the
  // anonymous box is checked the name/password rows hide (ReflectHandlerState).
  private loginScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('MULTIPLAYER LOGIN', s), s, 0, 6, 240));
    const pb = centeredBand(42, 156); // the prompt + field rows below already start at x=42 (in-band)
    stage.append(this.at(this.label('Enter Player Name and Password:', s), s, pb.x, 28, pb.w));

    if (!this.loginAnonymous) {
      // the "Player Name:" label box was clipped at 52u (ends x=94) and the EDIT started at
      // x=88, so the wider WI label ran UNDER the input. Widen the labels to 54u (ends x=96)
      // and start both EDITs at x=98 (narrower by 10u, right edge unchanged at 178).
      stage.append(this.at(this.label('Player Name:', s), s, 42, 56, 54));
      const nameIn = stageTextInput('loginName', 'Player name', this.loginName, s);
      nameIn.addEventListener('input', () => {
        this.loginName = nameIn.value;
      });
      stage.append(this.at(nameIn, s, 98, 55, 80, 11));
      stage.append(this.at(this.label('Password:', s), s, 42, 84, 54));
      const pwIn = stageTextInput('loginPassword', 'Password', this.loginPassword, s);
      pwIn.type = 'password';
      pwIn.addEventListener('input', () => {
        this.loginPassword = pwIn.value;
      });
      pwIn.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void this.submitLogin();
      });
      stage.append(this.at(pwIn, s, 98, 83, 80, 11));
    }

    // the "Login Anonymously" checkbox — toggling re-renders (rows show/hide). Clear any
    // stale sign-in status first: a failed credential login left "Sign-in failed…" set, and
    // toggling the box (or otherwise touching the form) used to re-render with it still shown,
    // making the user think the next attempt had failed too — the "failure message is still
    // there / I can't login" report.
    stage.append(
      this.at(
        this.squareCheck(
          'Login Anonymously',
          this.loginAnonymous,
          () => {
            c.clearStatus();
            this.loginAnonymous = !this.loginAnonymous;
            this.render();
          },
          s,
        ),
        s,
        42,
        112,
        156,
        10,
      ),
    );

    if (c.status.length > 0 && c.screen === 'login') {
      // a WRAPPING banner in the roomy gap BELOW the password field (ends y=94) and ABOVE the "Login
      // Anonymously" checkbox (starts y=112) — an 18u band, vs the cramped ~11u between the checkbox
      // and the button row where a two-line message overran the buttons (the button row actually
      // renders at ~y=133, not the 140 the layout comment implies — measured on the live client). A
      // 4u font (statusBanner wrap) keeps the long failure line to two lines; a bounded 15u height
      // (y=95..110) sits clear of both the password field above and the checkbox below.
      // pointer-events:none so it never swallows a tap; it reads right under the fields just typed.
      const lb = centeredBand(10, 220);
      stage.append(this.at(this.statusBanner(c.status, s, true), s, lb.x, 95, lb.w, 15));
    }
    // Bottom buttons — the canvas LoginForm::ReflectHandlerState (loginform.cpp:84-126) CENTRES the
    // visible buttons on ONE row, but the WI button font is wider than the canvas bitmap font, so a
    // named login's four buttons (LOGIN REGISTER FORGOT ACCOUNT CANCEL) overran narrow screens.
    // Keep LOGIN | REGISTER | CANCEL together on the primary row and drop the wide FORGOT ACCOUNT to
    // its own centred row below. REGISTER / FORGOT ACCOUNT (forms.ini.pp kidcRegister/kidcForgotAccount)
    // open the account web pages the shipped client points at (ui/loginform.ts → kszRegisterUrl /
    // kszForgotAccountUrl, the live /accounts/* worker) — PR #80's HTML rewrite dropped them; restored here.
    const lbtn = centeredBand(0, 240); // {40,160} under boost — keep the rows on-screen
    if (this.loginAnonymous) {
      this.buttonRow(stage, s, 140, 12, lbtn.x, lbtn.w, [
        {
          label: 'LOGIN',
          on: () => {
            void this.submitLogin();
          },
        },
        { label: 'CANCEL', on: () => this.d.onExit() },
      ]);
    } else {
      // Two rows with a 3u inter-row gap to MATCH the menu button stacks (e.g. the Play Game
      // screen's SINGLE PLAYER / MULTIPLAYER: 14u buttons on a 17u step = 3u gap). These login
      // buttons are 12u, so a 3u gap is a 15u step: row1 y=133 (ends 145), FORGOT ACCOUNT y=148
      // (3u below) — both still within the 160u form.
      this.buttonRow(stage, s, 133, 12, lbtn.x, lbtn.w, [
        {
          label: 'LOGIN',
          on: () => {
            void this.submitLogin();
          },
        },
        { label: 'REGISTER', on: () => this.d.openUrl?.(kszRegisterUrl) },
        { label: 'CANCEL', on: () => this.d.onExit() },
      ]);
      this.buttonRow(stage, s, 148, 12, lbtn.x, lbtn.w, [
        { label: 'FORGOT ACCOUNT', on: () => this.d.openUrl?.(kszForgotAccountUrl) },
      ]);
    }
    return stage;
  }

  private async submitLogin(): Promise<void> {
    await this.d.controller.login(this.loginAnonymous, this.loginName.trim(), this.loginPassword);
  }

  // ── CREATE ROOM (kidfCreateRoom) ─────────────────────────────────────────────
  // title (0,6,240) center; "Enter Room Name:" (42,28,52); "Room Name:" (42,56,52)
  // + EDIT (88,56,90); "Password:" (42,84,52) + EDIT (88,84,90); "Private" CHECKBOX
  // (42,112,57); OK (75,140,40,12) / CANCEL (125,140,40,12). Like CreateRoomForm
  // (createroomform.ts HideShowPasswordFields): "Private" GATES the password — a
  // private room is a password-protected one, so the Password row shows ONLY when
  // Private is checked and an empty password (a public room) is sent otherwise.
  // The wire is LOBBYCREATEROOM [roomname, password] (no separate private flag).
  private createRoomScreen(): HTMLElement {
    const c = this.d.controller,
      { stage, s } = this.makeStage();
    stage.append(this.at(formTitle('CREATE ROOM', s), s, 0, 6, 240));
    stage.append(this.at(this.label('Enter Room Name:', s), s, 42, 28, 156));

    stage.append(this.at(this.label('Room Name:', s), s, 42, 56, 52));
    const nameIn = stageTextInput('roomName', 'Room name', this.roomDraftName, s);
    nameIn.addEventListener('input', () => {
      this.roomDraftName = nameIn.value;
    });
    stage.append(this.at(nameIn, s, 88, 55, 90, 11));

    const submit = (): void => {
      const name = this.roomDraftName.trim();
      if (name.length > 0)
        void c.createRoom(name, this.roomDraftPrivate ? this.roomDraftPassword : '');
    };

    // Password row — shown only for a private (password-protected) room.
    if (this.roomDraftPrivate) {
      stage.append(this.at(this.label('Password:', s), s, 42, 84, 52));
      const pwIn = stageTextInput('roomPassword', 'Password', this.roomDraftPassword, s);
      pwIn.type = 'password';
      pwIn.addEventListener('input', () => {
        this.roomDraftPassword = pwIn.value;
      });
      pwIn.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') submit();
      });
      stage.append(this.at(pwIn, s, 88, 83, 90, 11));
    }

    stage.append(
      this.at(
        this.squareCheck(
          'Private',
          this.roomDraftPrivate,
          () => {
            this.roomDraftPrivate = !this.roomDraftPrivate;
            this.render();
          },
          s,
        ),
        s,
        42,
        112,
        156,
        10,
      ),
    );

    // OK / CANCEL — sized PER-LABEL (the shared buttonRow) + centred on the 240-form.
    const cb = centeredBand(2, 236);
    this.buttonRow(stage, s, 140, 12, cb.x, cb.w, [
      { label: 'OK', on: submit, en: this.roomDraftName.trim().length > 0 },
      { label: 'CANCEL', on: () => c.cancelCreateRoom() },
    ]);
    return stage;
  }

  // ── HOST MULTIPLAYER GAME (kidfCreateGame, a 160-WIDE form) ──────────────────
  // title "HOST MULTIPLAYER GAME" (31,6,99) center; "Game Speed:" (2,30) + SLIDER
  // (50,30,88,8) + speed value (140,30,17) right; "Choose a map to play on:"
  // (2,42); map LIST (2,52,156,76); CREATE GAME (17,140,60,12) / CANCEL
  // (93,140,50,12). The slider ranges over gatGameSpeeds[2..len-1-3] (the canvas
  // MP range), seeded from the saved game speed (clamped ≥4), like CreateGameForm.
  private createGameScreen(): HTMLElement {
    const c = this.d.controller,
      missions: MpMissionRow[] = c.mpMissions();
    const { stage, s } = this.makeStage(160);
    // creategameform.cpp — the missions split into the Challenge|Add-On radio tabs;
    // the selected map must belong to the active tab (switching tabs re-selects).
    const inTab = missions.filter((m) => m.category === this.createGameTab);
    if (this.selectedMapIndex === null || !inTab.some((m) => m.index === this.selectedMapIndex)) {
      this.selectedMapIndex = inTab[0]?.index ?? null;
    }
    // seed the speed notch from the saved game speed, clamped to the MP range.
    const lo = 2,
      hi = gatGameSpeeds.length - 1 - 3; // [2, 11] — the canvas SetRange
    if (this.gameSpeedNotch < 0) {
      let n = 8; // creategameform.cpp default SetValue(8)
      const t = Math.max(4, c.gameSpeed() || 0);
      for (let i = 0; i < gatGameSpeeds.length; i++)
        if (gatGameSpeeds[i] === t) {
          n = i;
          break;
        }
      this.gameSpeedNotch = Math.max(lo, Math.min(hi, n));
    }

    // the title gets a WIDE box centred on the 160-form (x=-20, w=200) so the long
    // Quadtrangle heading lays out on ONE line (item 8) instead of wrapping + overlapping.
    stage.append(this.at(formTitle('HOST MULTIPLAYER GAME', s), s, -20, 6, 200));

    // Game Name (kidcGameName, 2,18) — the canvas auto-names the game
    // "<player>'s game" (gameform sets the label) and marks the rename EDIT
    // <unimplemented>; render the label + the derived name (read-only, faithful).
    // The "Game Name:"/"Game Speed:" labels clipped in the authored 46/48u column
    // under the wider WI font, so the label column is widened to 54u and the value /
    // slider start are nudged from x=50 → x=56 to keep a clean gap.
    stage.append(this.at(this.label('Game Name:', s), s, 2, 18, 54));
    stage.append(this.at(this.label(`${c.playerName}'s game`, s), s, 56, 18, 102));

    // Game Speed slider — quantize a [0,1] drag to a notch in [lo, hi].
    stage.append(this.at(this.label('Game Speed:', s), s, 2, 30, 54));
    const span = hi - lo;
    const notchToFrac = (n: number): number => (span > 0 ? (n - lo) / span : 0);
    stage.append(
      this.at(
        this.sliderRow(
          notchToFrac(this.gameSpeedNotch),
          GetSpeedMultiplierString(gatGameSpeeds[this.gameSpeedNotch]),
          (f) => {
            this.gameSpeedNotch = lo + Math.round(f * span);
            return {
              frac: notchToFrac(this.gameSpeedNotch),
              label: GetSpeedMultiplierString(gatGameSpeeds[this.gameSpeedNotch]),
            };
          },
          s,
          'Game Speed',
          span,
        ),
        s,
        56,
        30,
        99,
        8,
      ),
    );

    // the Challenge|Add-On category tabs (creategameform.cpp kidcCategories radio
    // bar) — a centred segmented row, the active one underlined (the menu-view look).
    stage.append(this.at(this.categoryTabs(s), s, 0, 42, 160));

    // the map LIST for the active tab (2,52,156,68). An empty Add-On shows the
    // canvas kidcAddOnMessage centred inside (creategameform.cpp:417-424).
    const emptyMsg =
      this.createGameTab === 'addon'
        ? 'Install Add-On Mission Packs from the Main Menu'
        : 'No multiplayer maps found.';
    stage.append(
      this.at(
        this.listBox(
          inTab.map((m) => ({
            id: m.index,
            text: `${m.title || m.lvl} ${mapPlayersLabel(m)}`,
            selected: m.index === this.selectedMapIndex,
            onSelect: () => {
              this.selectedMapIndex = m.index;
            },
            onActivate: () => this.submitCreateGame(),
          })),
          emptyMsg,
          s,
          true,
          'Maps',
        ),
        s,
        2,
        52,
        156,
        68,
      ),
    );

    // the pack author/info label (creategameform.cpp kidcMissionPackInfo:2,127) —
    // "<title> by <author>" / "<pack> by Spiffcode, Inc." for the selected mission.
    const sel = inTab.find((m) => m.index === this.selectedMapIndex);
    const info = el('div', {
      ...labelStyle(s),
      opacity: '0.7',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'center',
    });
    info.textContent = sel?.info ?? '';
    stage.append(this.at(info, s, 2, 124, 156, 12));

    // auto-sized bottom row (CREATE GAME / CHAT / CANCEL) — the SHIPPED canvas form is the
    // wide kidfCreateGameWide, which carries a CHAT button (creategameform.ts:344-346 →
    // chatter_.ShowChat()) so the host can talk to the room while picking a map. CHAT opens
    // the shared full-screen chat overlay (controller.showChat), same as room/staging.
    this.buttonRow(stage, s, 140, 12, 2, 156, [
      {
        label: 'CREATE GAME',
        on: () => this.submitCreateGame(),
        en: this.selectedMapIndex !== null,
      },
      { label: this.chatLabel(), on: () => c.showChat(), minW: 32 },
      { label: 'CANCEL', on: () => c.cancelCreateGame() },
    ]);
    return stage;
  }

  // the CREATE GAME category radio bar "Challenge | Add-On" (creategameform.cpp
  // kidcCategories) — a centred segmented row, the active segment underlined.
  // Copied from the menu-view SelectMissionForm tab pattern.
  private categoryTabs(s: number): HTMLElement {
    const cats: { id: MpMissionCategory; name: string }[] = [
      { id: 'challenge', name: 'Challenge' },
      { id: 'addon', name: 'Add-On' },
    ];
    const tabs = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      whiteSpace: 'nowrap',
    });
    tabs.setAttribute('role', 'radiogroup'); // the kidcCategories RADIOBUTTONBAR → a radiogroup (a11y, visual-neutral)
    tabs.setAttribute('aria-label', 'Mission category');
    cats.forEach((cat, i) => {
      if (i > 0) {
        const sep = el('span', { color: 'rgba(232,238,240,0.4)', padding: `0 ${4 * s}px` });
        sep.textContent = '|';
        sep.setAttribute('aria-hidden', 'true');
        tabs.append(sep);
      }
      const active = cat.id === this.createGameTab;
      const t = el('button', {
        font: 'inherit',
        fontFamily: FONT_BUTTON,
        fontSize: `${Math.round(6 * s)}px`,
        letterSpacing: '0.04em',
        padding: `${1 * s}px ${1.5 * s}px`,
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: COL_FG,
        textDecoration: active ? 'underline' : 'none',
        textUnderlineOffset: `${1.5 * s}px`,
        textShadow: shadow,
      });
      t.textContent = cat.name;
      t.setAttribute('role', 'radio');
      t.setAttribute('aria-checked', active ? 'true' : 'false');
      t.addEventListener('click', () => {
        this.d.playSfx?.(ksfxGuiButtonTap);
        this.createGameTab = cat.id;
        this.selectedMapIndex = null;
        this.render();
      });
      tabs.append(t);
    });
    return tabs;
  }

  private submitCreateGame(): void {
    const c = this.d.controller;
    const m = c.mpMissions().find((x) => x.index === this.selectedMapIndex);
    if (m === undefined) return;
    void c.createGame(m.miid, gatGameSpeeds[this.gameSpeedNotch]);
  }

  // ── widgets ─────────────────────────────────────────────────────────────────
  // a centered form title rendered inside the design stage (scaled to s). The wider
  // WI title face (Quadtrangle) wrapped the longer headings ("HOST MULTIPLAYER GAME",
  // "WAIT FOR JOINING PLAYERS") to multiple lines that overlapped the form, so keep
  // titles on ONE line (nowrap) — the caller gives the title box enough width to hold
  // it (items 8/9 widen the title's own box past the form, centred on it).
  private centerText(text: string, s: number): HTMLElement {
    const e = el('div', { ...labelStyle(s), textAlign: 'center', opacity: '0.9' });
    e.textContent = text;
    return e;
  }

  // a transient status/notice banner (join failures, lobby chat lines) — the DOM analog of
  // the canvas ShowJoinMessage msgbox. ONE line (its box height is fixed by at(), the text is
  // flex-centred and clipped) so it can never grow into the button row, with an opaque-ish
  // backing so it reads over the list, and pointer-events:none so it never swallows a tap
  // meant for the buttons it sits near.
  private statusBanner(text: string, s: number, wrap = false): HTMLElement {
    const e = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      fontFamily: FONT,
      // wrap uses a SMALLER font (4u vs 5u): the WI face is very wide (~30 chars/line in this band),
      // so the login-failure line is 3 lines at 5u and overran the button row; at 4u it fits in ≤2
      // lines within the fixed height its caller gives it (verified in WebKit at the live scale).
      fontSize: `${Math.round((wrap ? 4 : 5) * s)}px`,
      lineHeight: wrap ? '1.15' : '1',
      color: COL_FG,
      textShadow: shadow,
      // wrap: multi-line for a long message (the login-failure line), bounded by the caller's fixed
      // at() height so it can't grow into the button row (overflow-hidden clips a rare 3rd line).
      // Non-wrap (room-join / chat banners): one clipped line so it can't grow into the buttons.
      whiteSpace: wrap ? 'normal' : 'nowrap',
      overflowWrap: wrap ? 'break-word' : 'normal',
      overflow: 'hidden',
      textOverflow: wrap ? 'clip' : 'ellipsis',
      background: 'rgba(7,22,27,0.9)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
      borderRadius: `${1.5 * s}px`,
      padding: wrap ? `${1 * s}px ${3 * s}px` : `0 ${3 * s}px`,
      boxSizing: 'border-box',
      pointerEvents: 'none',
    });
    e.textContent = text;
    return e;
  }

  private label(text: string, s: number): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = text;
    return e;
  }

  // a "Label: value" detail line (the canvas GameForm static labels).
  private detail(label: string, value: string, s: number): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    const k = el('span', { opacity: '0.7' });
    k.textContent = `${label} `;
    const v = el('span', { color: COL_FG });
    v.textContent = value;
    e.append(k, v);
    return e;
  }

  // a fixed-size scrolling list control: selectable rows (the solid steel-blue
  // highlight bar), an empty-state message centered inside the SAME box. The box
  // carries [data-list] so its rows are excluded from the form zip-in.
  private listBox(
    rows: {
      id: number;
      text: string;
      html?: HTMLElement;
      selected: boolean;
      onSelect?: () => void;
      onActivate?: () => void;
    }[],
    emptyMsg: string,
    s: number,
    clickable = true,
    ariaLabel?: string,
  ): HTMLElement {
    const box = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '0',
      overflowY: 'auto',
      boxSizing: 'border-box',
      width: '100%',
      height: '100%',
      background: 'rgba(7,22,27,0.55)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
    });
    box.dataset.list = '1';
    // a11y: a selectable list → role=listbox (the canvas ListControl); the non-selectable
    // roster is a plain list. Net-new, visual-neutral.
    box.setAttribute('role', clickable ? 'listbox' : 'list');
    if (ariaLabel !== undefined) box.setAttribute('aria-label', ariaLabel);
    if (rows.length === 0) {
      box.style.alignItems = 'center';
      box.style.justifyContent = 'center';
      const note = el('div', {
        ...labelStyle(s),
        opacity: '0.7',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: `0 ${4 * s}px`,
      });
      note.textContent = emptyMsg;
      box.append(note);
    } else {
      for (const r of rows)
        box.append(this.listItem(r.text, r.selected, r.onSelect, r.onActivate, s, r.html));
    }
    return box;
  }

  // a selectable scrolling-list row (the canvas ListControl item): a left-aligned
  // label (or a custom `content` element — the multi-column server row), the
  // SELECTED row a SOLID steel-blue bar with dark text (kiclrButtonFillHighlight)
  // — not a faint tint — click-to-select (with the select SFX) + double-click to
  // activate. Copied from menu-view.ts.
  private listItem(
    text: string,
    selected: boolean,
    onSelect: (() => void) | undefined,
    onActivate: (() => void) | undefined,
    s: number,
    content?: HTMLElement,
  ): HTMLElement {
    const interactive = onSelect !== undefined || onActivate !== undefined;
    const item = el('button', {
      textAlign: 'left',
      padding: `${1.5 * s}px ${4 * s}px`,
      background: selected ? 'rgb(122, 168, 198)' : 'transparent',
      color: selected ? '#0c1a22' : COL_FG,
      border: 'none',
      cursor: interactive ? 'pointer' : 'default',
      font: 'inherit',
      fontFamily: FONT,
      fontSize: `${Math.round(5 * s)}px`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flex: '0 0 auto',
      textShadow: selected ? 'none' : shadow,
    });
    if (content !== undefined) {
      content.style.color = selected ? '#0c1a22' : COL_FG;
      item.append(content);
    } else item.textContent = text;
    // a11y: an interactive row → role=option + aria-selected (the canvas ListControl item);
    // a non-interactive roster row → role=listitem. Net-new, visual-neutral.
    if (interactive) {
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.addEventListener('mouseenter', () => {
        if (!selected) item.style.background = 'rgba(143,208,224,0.14)';
      });
      item.addEventListener('mouseleave', () => {
        if (!selected) item.style.background = 'transparent';
      });
      // onSelectClick (not a bare click) so a retargeted cross-screen "ghost" tap — the
      // PLAY → MULTIPLAYER press whose synthesized click lands on the room row now under
      // the finger as the lobby renders — can't silently select a room (see onSelectClick).
      if (onSelect !== undefined)
        onSelectClick(item, () => {
          this.d.playSfx?.(ksfxGuiScrollingListSelectItem);
          onSelect();
          this.render();
        });
      if (onActivate !== undefined) item.addEventListener('dblclick', onActivate);
    } else {
      item.setAttribute('role', 'listitem');
      item.disabled = true;
      item.style.opacity = '0.95';
    }
    return item;
  }

  // a CHOOSE SERVER column header (chooseserverform.PositionColumns labels).
  private colHeader(text: string, align: 'left' | 'center' | 'right', s: number): HTMLElement {
    const e = el('div', {
      ...labelStyle(s),
      textAlign: align,
      opacity: '0.8',
      fontWeight: '700',
      whiteSpace: 'nowrap',
    });
    e.textContent = text;
    return e;
  }

  // a CHOOSE SERVER list row: name / location / status / player_count laid out in
  // columns aligned with the headers (the canvas tab-stopped ListControl item). `k`
  // narrows every column (default 1) so the row fits the centered portrait band.
  private serverRow(sv: ServerInfo, s: number, k = 1): HTMLElement {
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      fontSize: `${Math.round(5 * s)}px`,
    });
    const cell = (text: string, w: number, align: 'left' | 'center' | 'right'): HTMLElement => {
      const e = el('div', {
        width: `${w * k * s}px`,
        flex: '0 0 auto',
        textAlign: align,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      e.textContent = text;
      return e;
    };
    row.append(
      cell(sv.name, 92, 'left'),
      cell(sv.location, 62, 'left'),
      cell(sv.status, 40, 'center'),
      cell(String(sv.player_count), 34, 'right'),
    );
    return row;
  }

  // the canvas RadioButton/CheckBox look: a small square outlined box with an X
  // when set (menu-view's squareCheck, scaled to the design stage). Toggling
  // re-renders. Used for "Login Anonymously" + "Private".
  private squareCheck(
    label: string,
    checked: boolean,
    onToggle: () => void,
    s: number,
  ): HTMLElement {
    const row = el('label', {
      display: 'flex',
      alignItems: 'center',
      gap: `${3 * s}px`,
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      cursor: 'pointer',
    });
    const bx = Math.round(7 * s);
    const box = el('span', {
      width: `${bx}px`,
      height: `${bx}px`,
      border: `${Math.max(1, Math.round(s))}px solid ${COL_ACCENT}`,
      position: 'relative',
      flex: '0 0 auto',
      boxSizing: 'border-box',
    });
    if (checked) {
      const x = COL_ACCENT;
      box.append(
        el('span', {
          position: 'absolute',
          inset: `${Math.max(1, Math.round(s))}px`,
          background: `linear-gradient(45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%), linear-gradient(-45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%)`,
        }),
      );
    }
    const t = el('span', { whiteSpace: 'nowrap' });
    t.textContent = label;
    row.append(box, t);
    // a11y: expose the <label> as a focusable checkbox with its state + name (visual-neutral).
    row.setAttribute('role', 'checkbox');
    row.setAttribute('aria-checked', checked ? 'true' : 'false');
    row.setAttribute('aria-label', label);
    row.tabIndex = 0;
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
    }); // mousedown starts the selection — suppress it
    row.addEventListener('click', (e) => {
      e.preventDefault();
      this.d.playSfx?.(ksfxGuiCheckBoxTap);
      onToggle();
    });
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.d.playSfx?.(ksfxGuiCheckBoxTap);
        onToggle();
      }
    });
    return row;
  }

  // a horizontal slider row (the canvas SliderControl: a thin track + a small
  // thumb) with the value label on the right, scaled to the design stage and
  // sized to fill its `at()` box. `quantize` applies the dragged fraction to the
  // caller and returns the snapped fraction + label, updating its OWN thumb/label
  // during the drag (no mid-drag re-render). Adapted from menu-view's sliderRow.
  private sliderRow(
    initialFrac: number,
    initialLabel: string,
    quantize: (f: number) => { frac: number; label: string },
    s: number,
    ariaLabel?: string,
    notches?: number,
  ): HTMLElement {
    const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: `${3 * s}px`,
      width: '100%',
      height: '100%',
    });
    const track = el('div', { position: 'relative', height: '100%', cursor: 'pointer', flex: '1' });
    track.append(
      el('div', {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '50%',
        height: `${Math.max(1, Math.round(s))}px`,
        transform: 'translateY(-50%)',
        background: 'rgba(96,156,214,0.95)',
      }),
    );
    const thumb = el('div', {
      position: 'absolute',
      top: '50%',
      width: `${Math.max(2, Math.round(3 * s))}px`,
      height: '100%',
      transform: 'translate(-50%, -50%)',
      background: COL_ACCENT,
      left: `${clamp01(initialFrac) * 100}%`,
      boxShadow: '0 0 4px rgba(0,0,0,0.6)',
    });
    track.append(thumb);
    const value = el('div', {
      width: `${17 * s}px`,
      flex: '0 0 auto',
      textAlign: 'right',
      fontFamily: FONT,
      fontSize: `${Math.round(5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
    });
    value.textContent = initialLabel;
    // a11y: the SliderControl track → role=slider with the standard value props (the notch
    // index range + the live human readout). Visual-neutral; tabindex makes it keyboard-reachable.
    const span = notches !== undefined && notches > 0 ? notches : 1;
    track.setAttribute('role', 'slider');
    if (ariaLabel !== undefined) track.setAttribute('aria-label', ariaLabel);
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', String(span));
    track.tabIndex = 0;
    const setAria = (frac: number, label: string): void => {
      track.setAttribute('aria-valuenow', String(Math.round(clamp01(frac) * span)));
      track.setAttribute('aria-valuetext', label);
    };
    setAria(initialFrac, initialLabel);
    const commit = (frac: number): void => {
      const q = quantize(clamp01(frac));
      thumb.style.left = `${clamp01(q.frac) * 100}%`;
      value.textContent = q.label;
      setAria(q.frac, q.label);
    };
    const apply = (clientX: number): void => {
      const r = track.getBoundingClientRect();
      commit((clientX - r.left) / r.width);
    };
    let dragging = false;
    track.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dragging = true;
      this.d.playSfx?.(ksfxGuiButtonTap);
      track.setPointerCapture(e.pointerId);
      apply(e.clientX);
    });
    track.addEventListener('pointermove', (e) => {
      if (dragging) apply(e.clientX);
    });
    const end = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        /* capture already gone */
      }
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    // keyboard: ←/→ (and ↑/↓) nudge by one notch.
    track.addEventListener('keydown', (e) => {
      const dir =
        e.key === 'ArrowRight' || e.key === 'ArrowUp'
          ? 1
          : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
            ? -1
            : 0;
      if (dir === 0) return;
      e.preventDefault();
      this.d.playSfx?.(ksfxGuiButtonTap);
      const now = Number(track.getAttribute('aria-valuenow') ?? '0');
      commit((now + dir) / span);
    });
    row.append(track, value);
    return row;
  }

  // ── the "Enter Password:" prompt (the canvas HostInitiateAsk modal) ──────────
  // a dimmed overlay holding a centered design-stage dialog box (a solid
  // steel-blue title bar + white border + dark fill — the canvas DoModal chrome),
  // a password field, and OK / CANCEL. window.prompt is NOT used (it throws in a
  // cross-origin iframe) — same DOM-overlay pattern as the DLC custom-URL ask.
  private passwordPromptBox(p: { roomid: number; name: string; wrong: boolean }): HTMLElement {
    const c = this.d.controller;
    const { overlay, box, s } = this.modalBox(
      p.name !== '' ? `Join ${p.name}` : 'Join Room',
      190,
      70,
    );
    // a wrong prior attempt re-prompts with the "Incorrect password" hint
    // (the canvas RoomForm.ShowJoinMessage WrongPassword → re-ask loop).
    box.append(
      this.at(
        this.centerText(p.wrong ? 'Incorrect password. Enter Password:' : 'Enter Password:', s),
        s,
        6,
        16,
        178,
        12,
      ),
    );
    // This box is rebuilt by EVERY render, so it must be idempotent. The controller allocates
    // a FRESH prompt object per ask (including the wrong-password re-ask), so plain identity
    // separates "a new ask" — clear the draft, take focus once — from "a re-render", which
    // seeds the field back from the draft and leaves focus alone.
    if (p !== this.joinPrompt) {
      this.joinPrompt = p;
      this.joinPasswordDraft = '';
    }
    const input = stageTextInput('joinPassword', 'Password', this.joinPasswordDraft, s);
    input.type = 'password';
    input.addEventListener('input', () => {
      this.joinPasswordDraft = input.value;
    });
    const submit = (): void => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      // the LIVE field, not the draft: a password manager / autofill can populate the input
      // without firing `input`, and the draft would still be ''. The draft exists only to
      // re-seed the field across a rebuild.
      void c.submitJoinPassword(input.value);
    };
    input.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') submit();
    });
    const wrap = el('div', { width: '100%', height: '100%' });
    wrap.append(input);
    box.append(this.at(wrap, s, 6, 30, 178, 14));
    this.modalButtonRow(box, s, 52, 12, 190, [
      { label: 'OK', on: submit },
      { label: 'CANCEL', on: () => c.cancelJoinPassword() },
    ]);
    // one-shot: firing this on every rebuild re-stole focus and snapped the caret to the end
    this.joinAutofocus.apply(p, input);
    return overlay;
  }

  // a dimmed full-screen overlay + a centered design-stage dialog box (the canvas
  // DoModal SetTitleColor/Border/Background). [data-list] so it isn't form-zipped.
  // Copied from dlc-view.ts (the shared faithful modal chrome).
  private modalBox(
    title: string,
    w: number,
    h: number,
  ): { overlay: HTMLElement; box: HTMLElement; s: number } {
    const overlay = el('div', {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.45)',
      zIndex: '20',
    });
    overlay.dataset.list = '1';
    const s = formScale();
    const box = el('div', {
      position: 'relative',
      width: `${w * s}px`,
      height: `${h * s}px`,
      background: 'rgba(7,22,27,0.96)',
      border: `${Math.max(1, Math.round(s))}px solid #fff`,
      boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
    });
    const bar = el('div', {
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      height: `${12 * s}px`,
      background: 'rgb(60, 96, 150)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      fontSize: `${Math.round(6 * s)}px`,
      fontWeight: '700',
      letterSpacing: '0.06em',
    });
    bar.textContent = title;
    box.append(bar);
    overlay.append(box);
    return { overlay, box, s };
  }

  // a centred modal-box BUTTON ROW: each button SIZED to its label (buttonWidthU)
  // then centred across the box with an even gap. Copied from dlc-view.ts.
  private modalButtonRow(
    box: HTMLElement,
    s: number,
    y: number,
    h: number,
    boxW: number,
    btns: { label: string; on: () => void }[],
  ): void {
    const gapU = 8;
    const sized = btns.map((b) => ({ ...b, w: buttonWidthU(b.label, h, s) }));
    const totalW = sized.reduce((a, b) => a + b.w, 0) + gapU * (sized.length - 1);
    let x = Math.max(0, (boxW - totalW) / 2);
    for (const b of sized) {
      box.append(this.stageButton(s, b.label, x, y, b.w, h, b.on));
      x += b.w + gapU;
    }
  }

  // a text input styled like the chat input (a thin accent-bordered dark field), sized
  // to fill its `at()` box — the shared chrome.ts helper (also used by the chat overlay).
}

// the "(N players)" / "(N-M players)" suffix the CreateGameForm map list appends
// after each title (creategameform.cpp:285-287).
function mapPlayersLabel(m: MpMissionRow): string {
  return m.cPlayersMin === m.cPlayersMax
    ? `(${m.cPlayersMin} players)`
    : `(${m.cPlayersMin}-${m.cPlayersMax} players)`;
}

// the shared label text style (scaled to the stage).
function labelStyle(s: number): Partial<CSSStyleDeclaration> {
  return {
    fontFamily: FONT,
    fontSize: `${Math.round(5.5 * s)}px`,
    color: COL_FG,
    textShadow: shadow,
  };
}
